import { useEffect, useMemo, useRef, useState } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { api } from '../../api';
import { useApp } from '../../contexts/AppContext';
import { useNotepad } from '../../contexts/NotepadContext';
import { useAuth } from '../../contexts/AuthContext';
import { fetchFileUrl } from './fetchFile';
import { logMediaFetchStarted, logMediaFetchFailed } from './viewerDebug';
import BoxClipLayer from '../BoxClipLayer';
import NotebookDestinationSelect from '../NotebookDestinationSelect';

// Image renderer. Pan + pinch/wheel zoom for large scans. Fetches the
// image via api.raw so auth headers go through, then renders from a
// blob URL (revoked on unmount).
//
// User-controlled rotation lives here too: a quarter-turn button in the
// nav + `R` keybind cycles 0 → 90 → 180 → 270 → 0. Persisted on the
// server via PATCH /documents/{id}/rotation so reload + thumbnail tiles
// pick up the same orientation. Local optimistic update; if the PATCH
// fails we revert.
export default function ImagePreview({ documentId, manifest, doc }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [urlSource, setUrlSource] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [rotation, setRotation] = useState(doc?.display_rotation || 0);
  const [naturalDims, setNaturalDims] = useState(null);
  const imgRef = useRef(null);
  const { dispatch } = useApp();

  useEffect(() => {
    let cancelled = false;
    setImgUrl(null);
    setUrlSource(null);
    setLoadError(null);

    fetchFileUrl(documentId)
      .then(({ url, source }) => {
        if (cancelled) return;
        setImgUrl(url);
        setUrlSource(source);
        logMediaFetchStarted({ documentId, url_source: source });
      })
      .catch(err => {
        if (cancelled) return;
        setLoadError(err.message);
        logMediaFetchFailed({
          documentId,
          url_source: 'media_url_failed',
          error_class: err?.name || 'Error',
          error_message: err?.message,
        });
      });

    return () => { cancelled = true; };
  }, [documentId]);

  // Re-sync local rotation when a different doc is opened. (Same component
  // instance may be reused via key reconciliation in the parent.)
  useEffect(() => {
    setRotation(doc?.display_rotation || 0);
  }, [doc?.id, doc?.display_rotation]);

  const rotate = async () => {
    const next = (rotation + 90) % 360;
    const prev = rotation;
    setRotation(next);  // optimistic
    try {
      await api.patch(`/documents/${documentId}/rotation`, { rotation: next });
      // Push the new rotation into AppContext so the inventory tile reflects
      // it immediately instead of waiting for the next 30s poll.
      dispatch({
        type: 'PATCH_DOCUMENT',
        id: documentId,
        fields: { display_rotation: next },
      });
    } catch (err) {
      setRotation(prev);  // revert on failure
      // eslint-disable-next-line no-console
      console.warn('Rotation PATCH failed:', err?.message);
    }
  };

  // `R` (no modifier, not while typing) cycles rotation 90° CW. Ignores
  // input/textarea focus so it doesn't steal keystrokes from chat etc.
  useEffect(() => {
    const isEditableTarget = (t) => {
      if (!t) return false;
      const tag = (t.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || t.isContentEditable;
    };
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if ((e.key === 'r' || e.key === 'R') && !isEditableTarget(e.target)) {
        e.preventDefault();
        rotate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // rotate's closure captures `rotation`; depend on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotation, documentId]);

  const dimsLabel = manifest?.width && manifest?.height
    ? `${manifest.width} × ${manifest.height}`
    : null;

  // ── Attorney Notepad — box clips on this image ─────────────────────
  // Box capture/overlays are gated to rotation===0 so we don't have to map
  // normalized coords through the rotation transform; rotated views show a
  // hint instead. null notepad (app-level deep-link) disables entirely.
  const notepad = useNotepad();
  const { user } = useAuth();
  const myUid = user?.uid || null;
  const [clipMode, setClipMode] = useState(false);
  const [overlayVis, setOverlayVis] = useState('all');
  const boxesEnabled = rotation === 0;
  const imageBoxClips = useMemo(() => {
    if (!notepad || !boxesEnabled || overlayVis === 'off') return [];
    return notepad.clips.filter(c =>
      c.kind === 'box' &&
      c.document_id === documentId &&
      (overlayVis === 'all' || c.author_id === myUid)
    );
  }, [notepad, boxesEnabled, overlayVis, documentId, myUid]);

  const captureBox = (bbox) => {
    setClipMode(false);
    if (!notepad) return;
    notepad.captureClip({
      kind: 'box',
      document_id: documentId,
      anchor: { kind: 'bbox', bbox },
    });
  };

  // When rotated 90/270 we swap the layout box so react-zoom-pan-pinch's
  // Fit calculation accounts for the rotated bounding box. The img inside
  // is absolutely positioned and rotated about its own centre; the wrapper
  // takes the rotated bounding-box footprint.
  const isQuarterTurn = rotation === 90 || rotation === 270;
  const naturalW = naturalDims?.w || 0;
  const naturalH = naturalDims?.h || 0;
  const wrapperW = isQuarterTurn ? naturalH : naturalW;
  const wrapperH = isQuarterTurn ? naturalW : naturalH;

  return (
    <div className="dd-preview image-preview">
      {loadError && <p className="text-muted" style={{ padding: 16 }}>Failed to load image: {loadError}</p>}
      {!loadError && !imgUrl && <div className="dd-loading" style={{ padding: 16 }}>Loading image…</div>}
      {imgUrl && (
        <TransformWrapper
          minScale={0.1}
          maxScale={10}
          centerOnInit
          wheel={{ step: 0.15 }}
          doubleClick={{ mode: 'reset' }}
          // While drawing a notepad box, disable pan so the drag draws the box
          // instead of moving the image.
          panning={{ disabled: clipMode }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <div className="dd-preview-nav">
                <button className="btn" type="button" onClick={() => zoomOut()}>−</button>
                <button className="btn" type="button" onClick={() => resetTransform()}>Fit</button>
                <button className="btn" type="button" onClick={() => zoomIn()}>+</button>
                <button
                  className="btn"
                  type="button"
                  onClick={async () => { await rotate(); resetTransform(); }}
                  title="Rotate 90° clockwise (R)"
                  aria-label="Rotate 90 degrees clockwise"
                >
                  ⟳
                </button>
                {dimsLabel && (
                  <span style={{ marginLeft: 12, color: 'var(--text-muted)' }}>{dimsLabel}</span>
                )}
                {notepad && (
                  <>
                    <button
                      className={`btn ${clipMode ? 'np-tool-on' : ''}`}
                      type="button"
                      disabled={!boxesEnabled}
                      onClick={() => setClipMode(m => !m)}
                      title={boxesEnabled ? 'Draw a box to save a region to your notepad' : 'Reset rotation to clip a region'}
                    >✂ {clipMode ? 'Drag to clip…' : 'Clip to notepad'}</button>
                    <NotebookDestinationSelect />
                    <button
                      className="btn"
                      type="button"
                      onClick={() => setOverlayVis(v => v === 'all' ? 'mine' : v === 'mine' ? 'off' : 'all')}
                      title="Show saved clip highlights on the image — everyone's, only yours, or none"
                    >👁 Clips: {overlayVis === 'all' ? 'everyone' : overlayVis}</button>
                  </>
                )}
                <span style={{ flex: 1 }} />
                <span className="text-muted" style={{ fontSize: 12 }}>
                  R to rotate · scroll to zoom · drag to pan · double-click to reset
                </span>
              </div>
              <TransformComponent wrapperClass="image-preview-wrapper">
                {naturalDims && isQuarterTurn ? (
                  <div style={{ width: wrapperW, height: wrapperH, position: 'relative' }}>
                    <img
                      ref={imgRef}
                      src={imgUrl}
                      alt="Document preview"
                      draggable={false}
                      style={{
                        position: 'absolute',
                        width: naturalW,
                        height: naturalH,
                        left: '50%',
                        top: '50%',
                        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                        transformOrigin: 'center center',
                      }}
                      onLoad={(e) => setNaturalDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                      onError={async () => {
                        let status = null;
                        let errorClass = 'img_load_error';
                        try {
                          const credentials = urlSource === 'gcs_signed' ? 'omit' : 'include';
                          const r = await fetch(imgUrl, { method: 'HEAD', credentials });
                          status = r.status;
                        } catch (fetchErr) {
                          errorClass = fetchErr?.name || 'fetch_error';
                        }
                        logMediaFetchFailed({ documentId, url_source: urlSource, status, error_class: errorClass });
                      }}
                    />
                  </div>
                ) : (
                  <div className="np-img-wrap" style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
                  <img
                    ref={imgRef}
                    src={imgUrl}
                    alt="Document preview"
                    draggable={false}
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: 'center center',
                    }}
                    onLoad={(e) => setNaturalDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                    onError={async (e) => {
                      let status = null;
                      let errorClass = 'img_load_error';
                      try {
                        const credentials = urlSource === 'gcs_signed' ? 'omit' : 'include';
                        const r = await fetch(imgUrl, { method: 'HEAD', credentials });
                        status = r.status;
                      } catch (fetchErr) {
                        errorClass = fetchErr?.name || 'fetch_error';
                      }
                      logMediaFetchFailed({ documentId, url_source: urlSource, status, error_class: errorClass });
                    }}
                  />
                  {notepad && boxesEnabled && (clipMode || imageBoxClips.length > 0) && (
                    <BoxClipLayer
                      clips={imageBoxClips}
                      mode={clipMode ? 'capture' : 'view'}
                      myUid={myUid}
                      onCapture={captureBox}
                      onOpenClip={(clip) => notepad.openClip(clip.id)}
                    />
                  )}
                  </div>
                )}
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      )}
    </div>
  );
}
